<script>
/**
 * Deprecated dialog component
 * @deprecated Use the [card dialog](../dialogs/CardDialog.vue) instead
 */

export default {
  name: 'BaseDialog',
  props: {
    title: {
      type: String,
      default: null
    },
    hideCancelIcon: {
      type: Boolean
    },
    customLayout: {
      type: Boolean
    },
    width: {
      type: String,
      default: 'lg',
      /** @param {string} value */
      validator: (value) =>
        ['4xl', '3xl', '2xl', 'xl', 'lg', 'xs'].includes(value)
    },
    customFooterStyle: {
      type: Boolean
    }
  },
  emits: ['cancel', 'close'],
  data: () => ({
    show: false
  }),
  computed: {
    dialogClasses() {
      switch (this.width) {
        case '4xl':
          return 'sm:max-w-4xl max-w-full'
        case '3xl':
          return 'sm:max-w-3xl max-w-full'
        case '2xl':
          return 'sm:max-w-2xl max-w-full'
        case 'xl':
          return 'sm:max-w-xl max-w-full'
        case 'lg':
          return 'sm:max-w-lg max-w-full'
        case 'xs':
          return 'max-w-xs'
        default:
          return 'sm:max-w-lg max-w-full'
      }
    }
  },
  mounted() {
    this.show = true
  },
  methods: {
    cancel() {
      this.$emit('cancel')
      this.$emit('close')

      // Hacky-wacky solution to relay the cancel and close emits to the parent
      // so that we can avoid major refactoring to all components that use this
      // component since they all implement their own "cancelling" for some
      // weird reason. 🍝
      this.$parent?.$emit('cancel')
      this.$parent?.$emit('close')
    }
  }
}
</script>

<template>
  <Transition name="dialog-anim">
    <section v-if="show" class="dialog-curtain" @click.stop>
      <div
        v-if="!customLayout"
        role="dialog"
        :aria-label="title"
        class="dialog"
        :class="dialogClasses"
        @keydown.esc="cancel"
      >
        <section class="relative p-6 font-medium">
          <slot name="header"></slot>

          <template v-if="title">
            <h2 class="heading-3">{{ title }}</h2>
          </template>

          <template v-if="!hideCancelIcon">
            <button
              type="button"
              class="absolute top-0 right-0 mt-3 mr-3 size-8 text-2xl leading-none text-primary hover:text-secondary-700"
              @click.stop="cancel"
            >
              &times;
            </button>
          </template>
        </section>

        <div class="content">
          <slot></slot>
        </div>

        <section :class="customFooterStyle ? 'custom-footer' : 'footer'">
          <slot name="footer" v-bind="{ cancel }"> </slot>
        </section>
      </div>
      <div v-else role="dialog" class="dialog" :class="dialogClasses">
        <slot></slot>
      </div>
    </section>
  </Transition>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.dialog-curtain {
  @apply fixed flex w-full h-full bottom-0 left-0 right-0 justify-center items-center;
  z-index: 998;
  background-color: rgba(0, 0, 0, 0.4);
}

.dialog {
  @apply bg-white mt-20 lg:mt-24 flex rounded-sm flex-col w-full m-4;
  max-height: 88%;
  overscroll-behavior: contain;
}

.content {
  @apply px-6 pb-6 overflow-y-auto flex-1;
  height: 100%;
}

.footer {
  @apply flex justify-between border-t border-secondary-200;

  &:deep(button) {
    @apply grow h-auto py-5 leading-none rounded-none border-none;

    &:not([disabled]) {
      @apply bg-transparent text-primary;
    }

    &:not(:first-of-type) {
      @apply border-solid border-0 border-l border-secondary-200;
    }

    &:hover {
      @apply shadow-none bg-secondary-50;
    }

    &.loading::after {
      border-color: transparent transparent var(--color-primary)
        var(--color-primary) !important;
    }
  }
}

.custom-footer {
  @apply flex justify-between border-t border-secondary-200;

  &:deep(button) {
    @apply grow h-auto py-5 leading-none rounded-none border-none;

    &:hover {
      @apply shadow-none;
    }

    &.loading {
      @apply bg-success opacity-95;
    }
  }
}

.dialog-anim-enter-active,
.dialog-anim-leave-active,
.dialog-anim-enter-active .dialog,
.dialog-anim-leave-active .dialog {
  transition: all 0.25s;
}

.dialog-anim-enter-from, .dialog-anim-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.dialog-anim-enter-from .dialog,
.dialog-anim-leave-to .dialog {
  transform: translate(0, -5%) scale(1.05);
  -webkit-transform: translate(0, -5%) scale(1.05);
}

@variant sm {
  .dialog {
    min-width: 25rem;
  }
}

@variant lg {
  .dialog-curtain {
    padding-left: 256px;
  }
}
</style>
